import Chip from "@mui/material/Chip";
import { Theme, alpha } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import { CHIP_LABEL, DailyExpenseChipKind } from "./dailyExpenseStatus";

/** How much of the status colour seats a tinted chip without drowning its label. */
const SURFACE_OPACITY = 0.16;
const LOCK_ICON_SIZE = 14;

interface ChipTone {
  color: (theme: Theme) => string;
  background: (theme: Theme) => string;
  border: (theme: Theme) => string;
}

/**
 * The design rule's single chip set for every รับจ่าย screen: ยังไม่กรอก (outlined grey) · ร่าง
 * (grey) · รออนุมัติ (yellow) · อนุมัติ (green) · ตีกลับ (red) · ล็อก (dark grey with a lock).
 */
const TONE: Record<DailyExpenseChipKind, ChipTone> = {
  NOT_STARTED: {
    color: (theme) => theme.palette.text.secondary,
    background: (theme) => theme.palette.background.paper,
    border: (theme) => theme.palette.divider,
  },
  DRAFT: {
    color: (theme) => theme.palette.gray.main,
    background: (theme) => alpha(theme.palette.gray.main, SURFACE_OPACITY),
    border: () => "transparent",
  },
  SUBMITTED: {
    color: (theme) => theme.palette.warning.dark,
    background: (theme) => alpha(theme.palette.warning.main, SURFACE_OPACITY),
    border: () => "transparent",
  },
  APPROVED: {
    color: (theme) => theme.palette.success.dark,
    background: (theme) => alpha(theme.palette.success.main, SURFACE_OPACITY),
    border: () => "transparent",
  },
  REJECTED: {
    color: (theme) => theme.palette.error.dark,
    background: (theme) => alpha(theme.palette.error.main, SURFACE_OPACITY),
    border: () => "transparent",
  },
  LOCKED: {
    color: (theme) => theme.palette.common.white,
    background: (theme) => theme.palette.gray.main,
    border: () => "transparent",
  },
};

interface DailyExpenseStatusChipProps {
  kind: DailyExpenseChipKind;
  /** Another wording of the same state, e.g. ส่งและปิดเดือนแล้ว for a closed month on S4. */
  label?: string;
}

export default function DailyExpenseStatusChip({
  kind,
  label,
}: Readonly<DailyExpenseStatusChipProps>) {
  const tone = TONE[kind];

  return (
    <Chip
      size="small"
      variant="outlined"
      label={label ?? CHIP_LABEL[kind]}
      icon={kind === "LOCKED" ? <LockIcon sx={{ fontSize: LOCK_ICON_SIZE }} /> : undefined}
      sx={{
        fontWeight: 500,
        color: tone.color,
        bgcolor: tone.background,
        borderColor: tone.border,
        "& .MuiChip-icon": { color: "inherit" },
      }}
    />
  );
}
